import React, { useEffect, useState } from 'react';
import {
  Layout,
  Button,
  Table,
  Modal,
  Tooltip,
  Breadcrumb,
  Form,
  Upload,
  Drawer,
  Alert,
} from 'antd';
import { toast } from 'react-toastify';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, UploadProps } from 'antd';
import { Select } from 'antd';
const { Option } = Select;

import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
  FileImageOutlined,
  FolderAddOutlined,
  EditOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RcFile } from 'antd/es/upload';

//Components
import { Sidebar } from '../../../Components/Sidebar/Sidebar';
import { HeaderBar } from '../../../Components/Header/Header';
import Loader from '../../../Components/CoreComponents/Loader';
import FormItemInputComponent from '../../../Components/CoreComponents/FormItemInputComponent';
import { Map } from '../../../Components/CoreComponents/MapComponent';
import AnalyzingModal from '../../../Components/CoreComponents/AnalyzingModal';
import DrawingConfigModal from '../../../Components/CoreComponents/DrawingConfigModal';
import EditFileModal from '../../../Components/CoreComponents/EditFileModal';

//API
import {
  createDirectory,
  deleteDirectoryOrFile,
  getDirectoryList,
  uploadDrawing,
  getConversionStatus,
  getFile,
  convertDrawing,
} from '../../../Network/Core/Directory/DirectoryServices';

//CSS
import './ProjectDetail.css';
import AuthUser from '../../../Utils/user';
import { getProjectList } from '../../../Network/Core/Project/ProjectServices';

const { Content } = Layout;

interface FormValues {
  folderName: string;
}
interface UploadFormValues {
  fileName: string;
  simplifyThreshold: number;
  file: any;
}

export const ProjectDetailView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [formUploading, setFormUploading] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [folders, setFolders] = useState<DataType[]>([]);
  const [filename, setFilename] = useState('');
  const [inputFilename, setInputFilename] = useState('');
  const [form] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [isVisible, setVisible] = useState(false);
  const [selectedDrawing] = useState<DataType>();

  // Two-step upload flow state
  const [showAnalyzingModal, setShowAnalyzingModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentDrawingId, setCurrentDrawingId] = useState<string | null>(null);
  const [currentDrawingData, setCurrentDrawingData] = useState<DataType | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Edit file modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<DataType | null>(null);

  // Cleanup progress interval when modal closes
  useEffect(() => {
    if (!showAnalyzingModal && progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [showAnalyzingModal]);

  const navigate = useNavigate();
  const { Dragger } = Upload;

  const [formData, setFormData] = useState<FormValues>({
    folderName: '',
  });

  const [uploadFormData, setUploadFormData] = useState<UploadFormValues>({
    fileName: '',
    simplifyThreshold: 0.001,
    file: '',
  });

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const values = Form.useWatch([], form);
  const uploadValues = Form.useWatch([], uploadForm);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setIsFormValid(true);
      },
      () => {
        setIsFormValid(false);
      },
    );
  }, [values]);

  React.useEffect(() => {
    uploadForm.validateFields({ validateOnly: true }).then(
      () => {
        setUploading(true);
      },
      () => {
        setUploading(false);
      },
    );
  }, [uploadValues]);

  const { id } = useParams();
  const { confirm } = Modal;
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    setFormUploading(true);
    const customFormData = new FormData();
    fileList.forEach((file) => {
      customFormData.append('file', file as RcFile);
    });
    if (uploadFormData.fileName != '' && !uploadFormData.simplifyThreshold) {
      setUploading(true);
    }

    const directoryID = searchParams.get('directory');
    let directoryId = '';
    if (directoryID && directoryId === '') {
      directoryId = directoryID;
    }
    const drawing =
      '{"name": "' +
      uploadFormData.fileName +
      '", "parentId": "' +
      directoryId +
      '", "simplifyThreshold":' +
      uploadFormData.simplifyThreshold +
      '}';

    let projectId = '';

    if (typeof id === 'string' && id !== '') {
      projectId = id;
    }

    customFormData.append('projectId', projectId);
    customFormData.append('drawing', drawing);
    setShowUploadModal(false);

    // Show modal in uploading phase
    setUploadProgress(0);
    setShowAnalyzingModal(true);

    try {
      // Simulate upload progress (since uploadDrawing doesn't support progress tracking)
      // Progress slows down as it increases to handle varying file sizes (5s to 60s+)
      // Never reaches 100% - modal closes when analysis completes
      progressIntervalRef.current = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) return 95; // Cap at 95%, never show 100%
          // Slow down as progress increases
          if (prev < 30) return prev + 3;      // Fast start
          if (prev < 60) return prev + 2;      // Medium
          if (prev < 80) return prev + 1;      // Slow
          return prev + 0.5;                   // Very slow near end
        });
      }, 800);

      const response = await uploadDrawing(customFormData);
      // Progress keeps moving until modal closes

      const drawingId = response?.data?.id;

      if (!drawingId) {
        setShowAnalyzingModal(false);
        toast.error('Upload failed. Please try again.');
        setFormUploading(false);
        return;
      }

      // Reset form
      setFileList([]);
      setUploadFormData({
        fileName: '',
        file: '',
        simplifyThreshold: 0.001,
      });
      uploadForm.resetFields();
      uploadForm.setFieldsValue({
        simplifyThreshold: 0.001,
      });

      // Switch to analyzing phase
      setCurrentDrawingId(drawingId);
      setFormUploading(false);

      // Poll for analysis completion
      await pollForAnalysis(drawingId);
    } catch (error) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setShowAnalyzingModal(false);
      toast.error('Upload failed. Please try again.');
      setFormUploading(false);
    }
  };

  // Poll for analysis completion
  const pollForAnalysis = async (drawingId: string) => {
    const maxAttempts = 60; // 3 minutes (60 * 3s)
    const pollInterval = 3000;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      try {
        const response = await getFile(drawingId);
        const file = response?.data;
        consecutiveErrors = 0;

        if (!file) continue;

        if (file.status === 'AwaitingConfig') {
          // Stop progress interval and show 100% briefly before opening config
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setUploadProgress(100);
          await new Promise((resolve) => setTimeout(resolve, 500)); // Show 100% for 500ms
          
          setShowAnalyzingModal(false);
          setAnalysisResult(file.analysisResult);
          setCurrentDrawingData(file); // Store file data for use in handleConfigConfirm
          setShowConfigModal(true);

          // Refresh file list silently (no blur loader)
          const directoryId = searchParams.get('directory');
          if (directoryId) {
            await getDirectoryLists(directoryId, true);
          } else {
            await getProjectsList(id, true);
          }
          return;
        } else if (file.status === 'Failed') {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setShowAnalyzingModal(false);
          toast.error(file.failMessage || 'Analysis failed. Please try again.');
          return;
        }
        // Still analyzing, continue polling
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setShowAnalyzingModal(false);
          toast.error('Lost connection to the server. Please refresh and try again.');
          return;
        }
      }
    }

    // Timeout
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setShowAnalyzingModal(false);
    toast.error('Analysis timed out. Please try again.');
  };

  // Handle config modal confirm
  const handleConfigConfirm = async (config: {
    sourceUnit?: string;
    excludeLayers: string[];
    hideLayers: string[];
  }) => {
    if (!currentDrawingId) return;

    setShowConfigModal(false);
    
    // Immediately update file status to "Processing" so it appears in the table
    // (Files with "AwaitingConfig" are filtered out)
    // If file not in folders (backend may filter AwaitingConfig), add it
    setFolders((prev) => {
      const exists = prev.some((f) => f.id === currentDrawingId);
      if (exists) {
        return prev.map((f) =>
          f.id === currentDrawingId ? { ...f, status: 'Processing' } : f
        );
      } else if (currentDrawingData) {
        // Add file to list with Processing status
        return [{ ...currentDrawingData, status: 'Processing' }, ...prev];
      }
      return prev;
    });
    
    const toastId = toast.loading('Uploading drawing...');

    try {
      const response = await convertDrawing(currentDrawingId, config);

      if (response?.data?.status === 'Processing') {
        // Also refresh file list from backend to ensure consistency
        // (in case immediate update didn't work)
        const directoryId = searchParams.get('directory');
        if (directoryId) {
          getDirectoryLists(directoryId, true);
        } else {
          getProjectsList(id, true);
        }
        // Start polling for conversion completion
        pollForConversion(currentDrawingId, toastId);
      } else {
        toast.update(toastId, {
          render: response?.data?.error || 'Conversion failed',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        // Set status to Failed so row stays visible (not hidden like AwaitingConfig)
        setFolders((prev) =>
          prev.map((f) =>
            f.id === currentDrawingId ? { ...f, status: 'Failed' } : f
          )
        );
      }
    } catch (error) {
      console.error('Convert error:', error);
      toast.update(toastId, {
        render: 'Conversion failed. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
      // Set status to Failed so row stays visible (not hidden like AwaitingConfig)
      setFolders((prev) =>
        prev.map((f) =>
          f.id === currentDrawingId ? { ...f, status: 'Failed' } : f
        )
      );
    }

    setCurrentDrawingId(null);
    setCurrentDrawingData(null);
    setAnalysisResult(null);
  };

  // Poll for conversion completion
  const pollForConversion = async (drawingId: string, toastId: any) => {
    const maxAttempts = 60; // 3 minutes (60 * 3s)
    const pollInterval = 3000;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      try {
        const response = await getFile(drawingId);
        const file = response?.data;
        consecutiveErrors = 0;

        if (!file) continue;

        if (file.status === 'Completed') {
          toast.update(toastId, {
            render: 'Drawing uploaded successfully!',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });

          // Refresh file list silently (no blur loader)
          const directoryId = searchParams.get('directory');
          if (directoryId) {
            await getDirectoryLists(directoryId, true);
          } else {
            await getProjectsList(id, true);
          }
          return;
        } else if (file.status === 'Failed') {
          toast.update(toastId, {
            render: file.failMessage || 'Conversion failed. Please try again.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
          return;
        }
        // Still processing, continue polling
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          toast.update(toastId, {
            render: 'Lost connection. Please refresh to check the status.',
            type: 'warning',
            isLoading: false,
            autoClose: 5000,
          });
          return;
        }
      }
    }

    // Timeout
    toast.update(toastId, {
      render: 'Conversion is taking longer than expected. Please refresh to check the status.',
      type: 'warning',
      isLoading: false,
      autoClose: 5000,
    });
  };

  // Handle config modal cancel
  const handleConfigCancel = async () => {
    setShowConfigModal(false);
    setAnalysisResult(null);
    // Delete the uploaded file (new upload flow only)
    if (currentDrawingId) {
      try {
        await deleteDirectoryOrFile(currentDrawingId);
        toast.info('Upload cancelled');
        // Refresh file list silently (no blur loader)
        const directoryId = searchParams.get('directory');
        if (directoryId) {
          await getDirectoryLists(directoryId, true);
        } else {
          await getProjectsList(id, true);
        }
      } catch (error) {
        toast.error('Failed to cancel upload. Please refresh the page.');
      }
    }
    setCurrentDrawingId(null);
    setCurrentDrawingData(null);
  };

  useEffect(() => {
    const directoryID = searchParams.get('directory');
    if (directoryID) {
      void getDirectoryLists(directoryID);
    } else {
      void getProjectsList(id);
    }
  }, [searchParams.get('directory'), showFolderModal]);

  // Auto-poll for files with "Processing" or "Analyzing" status
  useEffect(() => {
    const processingFiles = folders.filter(
      (file: any) => file.status === 'Processing' || file.status === 'Analyzing',
    );

    if (processingFiles.length === 0) {
      return; // No files to poll
    }

    const pollInterval = setInterval(async () => {
      let hasUpdates = false;

      for (const file of processingFiles) {
        try {
          const response = await getConversionStatus(file.id);
          if (response?.data?.status && response.data.status !== 'processing') {
            hasUpdates = true;
            break; // Found an update, refresh the list
          }
        } catch (error) {
          console.error('Error polling conversion status:', error);
        }
      }

      if (hasUpdates) {
        // Refresh the file list silently (no blur loader)
        const directoryID = searchParams.get('directory');
        if (directoryID) {
          void getDirectoryLists(directoryID, true);
        } else {
          void getProjectsList(id, true);
        }
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [folders, searchParams, id]);

  const authUser = AuthUser.getAuthUser();
  if (!authUser) return null;

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleUploadFormChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUploadFormData({
      ...uploadFormData,
      [event.target.name]: event.target.value,
    });
  };

  const getBreadcrumb = () => {
    const breadcrumb = [
      {
        title:
          authUser.role === 'admin' ? 'Projects' : 'Dashboard',
        href:
          authUser.role === 'admin'
            ? '/projects-list'
            : '/dashboard',
      },
      {
        title: searchParams.get('projectName'),
        href:
          '/view-project-details/' +
          id +
          '?projectName=' +
          searchParams.get('projectName'),
      },
    ];
    const currentBreadcrumb = searchParams.get('breadcrumb');

    if (currentBreadcrumb) {
      const currentBreadcrumbPath = currentBreadcrumb
        .trim()
        .replace(/^>/, '')
        .split('>');

      let setNewBreadcrumb = '';
      currentBreadcrumbPath.map(function (item, i) {
        setNewBreadcrumb = setNewBreadcrumb + '>' + item;
        const currentCrumb = item.split('-path-');

        const currentProjectName = searchParams.get('projectName');
        if (i + 1 !== currentBreadcrumbPath.length) {
          breadcrumb.push({
            title: currentCrumb[0],
            href:
              '/view-project-details/' +
              id +
              '?projectName=' +
              currentProjectName +
              '&directory=' +
              currentCrumb[1] +
              '&breadcrumb=' +
              setNewBreadcrumb,
          });
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          breadcrumb.push({
            title: currentCrumb[0],
          });
        }
      });
    }

    return breadcrumb;
  };

  const props: UploadProps = {
    name: 'file',
    multiple: false,

    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);

      setUploadFormData({
        ...uploadFormData,
        ['file']: null,
      });
      uploadForm.setFieldsValue({
        file: '',
      });
    },
    beforeUpload: async (file) => {
      const fileExtension = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();
      const fileName = file.name
        .split('.')
        .slice(0, -1)
        .join('.')
        .substring(0, 50);
      const fileSize = file.size;

      if (fileSize > 1024 * 1024 * 20) {
        uploadForm.setFields([
          {
            name: 'file',
            errors: ['File must be smaller than 20MB'],
          },
        ]);
        return Upload.LIST_IGNORE; // Prevent the file from being added to the list
      }

      const allowedExtensions = ['.dwg'];
      if (!allowedExtensions.includes(fileExtension)) {
        uploadForm.setFields([
          {
            name: 'file',
            errors: [
              'File type is not supported. Please upload a valid DWG file.',
            ],
          },
        ]);
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      setFilename(fileName);
      setUploadFormData({
        ...uploadFormData,
        ['file']: file,
        ['fileName']: fileName,
      });

      uploadForm.setFieldsValue({
        fileName: fileName,
        file: file,
      });

      void uploadForm.validateFields();

      setUploading(true);

      return false; // Prevent automatic upload
    },
    fileList,
  };

  const showModal = () => {
    form.resetFields();
    form.setFieldsValue({
      folderName: '',
    });
    setShowFolderModal(true);
  };

  const handleOk = () => {
    setShowFolderModal(false);
  };

  const handleCancel = () => {
    setShowFolderModal(false);
  };

  const onshowUploadModal = () => {
    uploadForm.resetFields();
    uploadForm.setFieldsValue({
      simplifyThreshold: 0.001,
      file: '',
      fileName: '',
    });
    setShowUploadModal(true);
  };

  const handleUploadOk = () => {
    setShowUploadModal(false);
  };

  const onFinish = () => {
    const directoryID = searchParams.get('directory');
    if (directoryID) {
      void createFolder(formData.folderName, directoryID);
      if (directoryID) {
        void getDirectoryLists(directoryID);
      } else {
        void getProjectsList(id);
      }
    } else {
      void createFolder(formData.folderName);
    }
    setShowFolderModal(false);
  };
  const formatDate = (string: string) => {
    return new Date(string).toLocaleDateString([], {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleUploadCancel = () => {
    uploadForm.resetFields();
    setUploadFormData({
      fileName: '',
      file: '',
      simplifyThreshold: 0.001,
    });
    setFileList([]);
    setShowUploadModal(false);
  };

  const viewDrawing = (record: DataType) => {
    const data = { id: record.id };
    // Use file ID in URL (not project ID) so URL is shareable/bookmarkable
    navigate('/view-drawing/' + record.id + getCurrentParams(), {
      state: data,
    });
  };

  const onEditClick = (file: DataType) => {
    setEditingFile(file);
    setEditModalOpen(true);
  };

  const getCurrentParams = () => {
    return location.search;
  };

  const viewDirectory = (directoryId: string, directoryName: string) => {
    setFormUploading(true);
    const currentBreadcrumb = searchParams.get('breadcrumb')
      ? searchParams.get('breadcrumb')
      : '';

    navigate(
      '/view-project-details/' +
        id +
        '?projectName=' +
        searchParams.get('projectName') +
        '&directory=' +
        directoryId +
        '&breadcrumb=' +
        currentBreadcrumb +
        '>' +
        directoryName +
        '-path-' +
        directoryId,
    );
    void getDirectoryLists(directoryId);
    setFormUploading(false);
  };
  interface DataType {
    createdAt: string;
    createdBy: string;
    id: string;
    status: string;
    updatedAt: string;
    name: string;
    projectId: string;
    parentId: string;
    type: string;
    deleted: boolean;
    simplifiedGeojson: string;
    originalDWG: string;
    asBuilt: string;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: 'a-tag',
      sorter: (a, b) => a.name.localeCompare(b.name),
      onCell: (record) => {
        let isDragging = false;

        return {
          onMouseDown: (e) => {
            isDragging = false; // Reset the dragging flag on mouse down
          },
          onMouseMove: (e) => {
            // Check if the mouse is being dragged
            if (e.buttons === 1) {
              // Left mouse button is pressed
              isDragging = true;
            }
          },
          onMouseUp: (e) => {
            // Execute the click action only if not dragging
            if (!isDragging) {
              if (!record.status || record.status === 'Completed') {
                if (record.type === 'directory') {
                  viewDirectory(record.id, record.name);
                } else {
                  viewDrawing(record);
                }
              }
            }
          },
        };
      },
      render: (_, record) => (
        <>
          {record.type === 'directory' ? (
            <>
              {/* <span 
               className="a-tag"
               onClick={() => viewDirectory(record.id, record.name)}
             >
            */}
              <FolderAddOutlined
                style={{
                  opacity: record.status !== 'Completed' ? 0.65 : 1.0,
                }}
                key={record.id}
              />
              {' ' + record.name}
              {/* // </span> */}
            </>
          ) : (
            <>
              {/* <Link
                className="a-tag"
                to={'/view-drawing/' + id + getCurrentParams()}
                state={record}
              > */}
              {/* <span className="a-tag" onClick={() => viewDrawing(record)}> */}
              <FileImageOutlined
                style={{ opacity: record.status !== 'Completed' ? 0.65 : 1.0 }}
                key={record.id}
              />{' '}
              {' ' + record.name}
              {/* </span> */}
              {/* </Link> */}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: 'a-tag',
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      onCell: (record) => {
        return {
          onClick: () => {
            if (!record.status || record.status === 'Completed') {
              if (record.type === 'directory') {
                viewDirectory(record.id, record.name);
              } else {
                viewDrawing(record);
              }
            }
          },
        };
      },
      render: (_, record) => (
        <>
          <span style={{ opacity: record.status !== 'Completed' ? 0.65 : 1.0 }}>
            {formatDate(record.createdAt)}
          </span>
        </>
      ),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      className: 'a-tag',
      sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
      onCell: (record) => {
        return {
          onClick: () => {
            if (!record.status || record.status === 'Completed') {
              if (record.type === 'directory') {
                viewDirectory(record.id, record.name);
              } else {
                viewDrawing(record);
              }
            }
          },
        };
      },
      render: (_, record) => (
        <>
          <span style={{ opacity: record.status !== 'Completed' ? 0.65 : 1.0 }}>
            {record.createdBy}
          </span>
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      className: 'a-tag',
      sorter: (a, b) => (!a.status ? 1 : a.status.localeCompare(b.status)),
      onCell: (record) => {
        return {
          onClick: () => {
            if (!record.status || record.status === 'Completed') {
              if (record.type === 'directory') {
                void viewDirectory(record.id, record.name);
              } else {
                viewDrawing(record);
              }
            }
          },
        };
      },
      render: (_, record) => (
        <>
          {record.status && (
            <span
              className={`status 
              ${record.status !== 'Completed' ? 'not-completed' : ''} 
              ${record.status.toLowerCase()}`}
              style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
            >
              {(record.status === 'Processing' || record.status === 'Analyzing') && (
                <LoadingOutlined spin style={{ marginRight: 8 }} />
              )}
              {record.status === 'AwaitingConfig' ? 'Awaiting Config' : record.status}
            </span>
          )}
        </>
      ),
    },

    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      className: 'a-tag',
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt).getTime();
        return dateA - dateB;
      },
      onCell: (record) => ({
        onClick: () => {
          if (!record.status || record.status === 'Completed') {
            if (record.type === 'directory') {
              viewDirectory(record.id, record.name);
            } else {
              viewDrawing(record);
            }
          }
        },
      }),
      render: (_, record) => {
        const displayDate = record.updatedAt || record.createdAt;
        return (
          <span style={{ opacity: record.status !== 'Completed' ? 0.65 : 1.0 }}>
            {formatDate(displayDate)}
          </span>
        );
      },
    },

    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <>
          {record.type === 'directory' && (
            <div className="action-box">
              <span style={{ width: '42px' }} />
              <Tooltip title="Edit Folder">
                <Button
                  className="action-btn"
                  onClick={() => onEditClick(record)}
                >
                  <EditOutlined className="view-project-btn" />
                </Button>
              </Tooltip>
              <Tooltip title="Delete Directory">
                <Button
                  className="action-btn"
                  onClick={() => showDeleteConfirm(record.name, record.id)}
                >
                  <DeleteOutlined className="view-project-btn" />
                </Button>
              </Tooltip>
            </div>
          )}
          {record.type === 'drawing' && (
            <div className="action-box" key={'delete' + record.id}>
              <Tooltip title="Edit Drawing">
                <Button
                  className="action-btn"
                  onClick={() => onEditClick(record)}
                >
                  <EditOutlined className="view-project-btn" />
                </Button>
              </Tooltip>
              <Tooltip title="Delete Drawing">
                <Button
                  className="action-btn"
                  onClick={() => showDeleteConfirm(record.name, record.id)}
                >
                  <DeleteOutlined className="view-project-btn" />
                </Button>
              </Tooltip>
            </div>
          )}
        </>
      ),
    },
  ];

  const getDirectoryLists = async (directoryId: string | undefined = '', silent = false) => {
    if (!silent) {
      setIsLoading(true);
      setFormUploading(true);
    }
    try {
      const response = await getDirectoryList(directoryId);
      const data = response?.data;
      if (!data?.files) {
        if (!silent) toast.error('Failed to load files');
        return;
      }
      // Sort by updatedAt descending (most recently updated first)
      const sortedFiles = [...data.files].sort((a: any, b: any) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setFolders(sortedFiles);
    } catch (error: any) {
      if (!silent) {
        toast.error('Failed to load files');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
        setFormUploading(false);
      }
    }
  };

  const getProjectsList = async (projectId: string | undefined = '', silent = false) => {
    if (!silent) {
      setIsLoading(true);
      setFormUploading(true);
    }
    try {
      const response = await getProjectList(projectId);
      const data = response?.data;
      if (!data?.files) {
        if (!silent) toast.error('Failed to load files');
        return;
      }
      // Sort by updatedAt descending (most recently updated first)
      const sortedFiles = [...data.files].sort((a: any, b: any) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setFolders(sortedFiles);
    } catch (error: any) {
      if (!silent) {
        toast.error('Failed to load files');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
        setFormUploading(false);
      }
    }
  };

  const showDeleteConfirm = (name: string, id: string) => {
    confirm({
      title: 'Are you sure you want to delete ' + name + '?',
      wrapClassName: 'delete-modal-wrapper',
      className: 'delete-modal',
      okText: 'Yes',
      cancelText: 'No',
      width: 592,
      centered: true,
      closable: true,
      onOk() {
        void deleteDirectory(id);
      },
      onCancel() {
        console.warn('Cancel');
      },
    });
  };

  const deleteDirectory = async (directoryId: string | undefined = '') => {
    setFormUploading(true);
    try {
      await deleteDirectoryOrFile(directoryId);
      toast.success('Deleted successfully');
      const dirId = searchParams.get('directory');
      if (dirId) {
        await getDirectoryLists(dirId);
      } else {
        await getProjectsList(id);
      }
    } catch (error: any) {
      toast.error('Failed to delete. Please try again.');
    } finally {
      setIsLoading(false);
      setFormUploading(false);
    }
  };

  const createFolder = async (
    folderName: string = '',
    directoryId: string = '',
  ) => {
    setFormUploading(true);
    try {
      await createDirectory({
        projectId: id,
        directory: {
          name: folderName,
          parentId: directoryId,
        },
      });

      toast.success('Directory created successfully');

      setFormData({
        ...formData,
        ['folderName']: '',
      });
      form.resetFields();

      if (directoryId) {
        await getDirectoryLists(directoryId);
      } else {
        await getProjectsList(id);
      }
    } catch (error: any) {
      toast.error('Failed to create folder. Please try again.');
    } finally {
      setFormUploading(false);
    }
  };

  const handleClick = () => {
    setInputFilename(filename);
    uploadForm.setFieldsValue({ fileName: filename });
  };

  return (
    <>
      {isLoading && <Loader />}
      <Layout className="layout_wrapper">
        <HeaderBar activePopupLink="" />
        <Layout>
          <Sidebar
            currentVal={
              authUser.role === 'user'
                ? 'dashboard'
                : 'projects-list'
            }
          />
          <Content>
            <Breadcrumb separator=">" items={getBreadcrumb()} />

            <div className="page-content-header">
              <h3>Project Details</h3>
              <div className="pagecontent-header-rightbox">
                <Button
                  disabled={formUploading}
                  className="common-btn"
                  onClick={showModal}
                >
                  Create Folder
                </Button>
                <Button
                  className="common-btn"
                  onClick={onshowUploadModal}
                  disabled={formUploading}
                >
                  <UploadOutlined /> Upload File
                </Button>
              </div>
            </div>
            <div className="list-table">
              <Table
                columns={columns}
                rowKey="id"
                tableLayout="auto"
                dataSource={folders.filter((f: any) => f.type === 'directory' || !['AwaitingConfig', 'Analyzing'].includes(f.status))}
                pagination={false}
                // scroll={{ x: 'max-content' }}
              />
            </div>
          </Content>
          {/* <Footer style={{ textAlign: 'center' }}>Ant Design ©2023 Created by Ant UED</Footer> */}

          {/* create folder modal */}
          <Modal
            centered
            title="Create Folder"
            footer={null}
            open={showFolderModal}
            onOk={handleOk}
            onCancel={handleCancel}
            width={600}
          >
            <Form
              form={form}
              layout="vertical"
              className="common-form"
              name="create-folder-form"
              onSubmitCapture={onFinish}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <FormItemInputComponent
                label={null}
                name="folderName"
                className=""
                onChangeFunction={handleFormChange}
                placeholder="Enter Folder Name"
                inputType="text"
                // initialValues={formData.folderName}
                // suffix={<MailOutlined />}
                rules={[
                  { required: true, message: 'Folder Name is required' },
                  {
                    max: 30,
                    message: 'Folder Name must be max 30 characters.',
                  },
                  {
                    pattern: new RegExp(
                      '^[A-Za-z0-9- _]*[A-Za-z0-9][A-Za-z0-9 _]*$',
                    ),
                    message: 'Special character not allowed',
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (value && folders.some((f: DataType) => f.name === value.trim())) {
                        return Promise.reject(new Error('A folder with this name already exists'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
              <div className="form-btns">
                <Button className="self-btn" onClick={handleCancel}>
                  cancel
                </Button>
                <Button
                  className="common-btn"
                  htmlType="submit"
                  disabled={!isFormValid}
                >
                  Ok
                </Button>
              </div>
            </Form>
          </Modal>
          {/* create folder modal */}

          {/* Upload file modal */}
          <Modal
            centered
            title="Upload File"
            footer={null}
            open={showUploadModal}
            onOk={handleUploadOk}
            onCancel={handleUploadCancel}
            width={600}
          >
            <Form
              form={uploadForm}
              layout="vertical"
              preserve={false}
              className="common-form"
              name="upload-file-form"
              onSubmitCapture={handleUpload}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <FormItemInputComponent
                label={<span style={{ fontWeight: 600 }}>File Name</span>}
                name="fileName"
                id="filename"
                className=""
                onChangeFunction={handleUploadFormChange}
                placeholder="Enter File Name"
                inputType="text"
                value={inputFilename}
                required={false}
                rules={[
                  { required: true, message: 'File Name is required' },
                  {
                    max: 50,
                    message: 'File Name must be max 50 characters.',
                  },
                  {
                    pattern: new RegExp(
                      '^[A-Za-z0-9- _]*[A-Za-z0-9][A-Za-z0-9 _]*$',
                    ),
                    message: 'Special character not allowed',
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (value && folders.some((f: DataType) => f.name === value.trim())) {
                        return Promise.reject(new Error('A file with this name already exists'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />

              <div className="form-button">
                <button type="button" onClick={handleClick}>
                  Use name from file
                </button>
              </div>

              <Form.Item
                label={
                  <span style={{ fontWeight: 600 }}>
                    Curved Element Resolution{' '}
                    <Tooltip
                      title="Higher values add more points along curved elements"
                      placement="top"
                      color="#333"
                      overlayStyle={{ maxWidth: 400 }}
                      overlayInnerStyle={{ fontSize: 13 }}
                      zIndex={2000}
                    >
                      <InfoCircleOutlined style={{ fontSize: 14, color: '#1890ff', cursor: 'help' }} />
                    </Tooltip>
                  </span>
                }
                name="simplifyThreshold"
                required={false}
                rules={[
                  { required: true, message: 'Please select a Resolution!' },
                ]}
                initialValue={0.001} // Matches exactly "Medium Detail"
              >
                <Select
                  placeholder="Select Resolution"
                  onChange={(value: number) => {
                    setUploadFormData({
                      ...uploadFormData,
                      simplifyThreshold: value,
                    });
                  }}
                >
                  <Option value={0.0001}>High</Option>
                  <Option value={0.001}>Default</Option>
                  <Option value={0.01}>Low</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="file"
                rules={[
                  {
                    required: true,
                    message: 'Please select the drawing file',
                  },
                ]}
              >
                <Dragger accept=".dwg" {...props}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              </Form.Item>

              <div className="form-btns">
                <Button className="self-btn" onClick={handleUploadCancel}>
                  Cancel
                </Button>
                <Button
                  className="common-btn"
                  htmlType="submit"
                  disabled={!uploading}
                >
                  Continue
                </Button>
              </div>
            </Form>
          </Modal>
          {/* Upload file modal */}

          {/* Analyzing Modal */}
          <AnalyzingModal
            open={showAnalyzingModal}
            uploadProgress={uploadProgress}
          />

          {/* Config Modal */}
          <DrawingConfigModal
            open={showConfigModal}
            analysisResult={analysisResult}
            onConfirm={handleConfigConfirm}
            onCancel={handleConfigCancel}
          />

          {/* Edit File Modal */}
          <EditFileModal
            open={editModalOpen}
            file={editingFile}
            siblingNames={folders.filter((f: DataType) => f.id !== editingFile?.id).map((f: DataType) => f.name)}
            onClose={() => {
              setEditModalOpen(false);
              setEditingFile(null);
            }}
            onSuccess={() => {
              const directoryId = searchParams.get('directory');
              if (directoryId) {
                getDirectoryLists(directoryId, true);
              } else {
                getProjectsList(id, true);
              }
            }}
            onFileUpdate={(fileId, updates) => {
              // Update the file's fields in-place without refreshing the list
              setFolders((prev) =>
                prev.map((f) =>
                  f.id === fileId ? { ...f, ...updates } : f
                )
              );
            }}
          />

          <Drawer
            open={isVisible}
            onClose={() => setVisible(false)}
            width="100VW"
          >
            <Content>
              <Map selectedDrawing={selectedDrawing} />
            </Content>
          </Drawer>
        </Layout>
      </Layout>
    </>
  );
};
